import { CollectionIcon, GiftIcon, MenuIcon } from "@xola/icons";
import { Badge } from "@xola/ui-kit";
import { isPackage } from "common";
import { isBaseGiftProduct, isMerchandiseProduct, isPartnerPackageProduct } from "common/src/models/Product";
import React, { ReactNode, useEffect, useState } from "react";
import { CollectionNode, Product, ProductObject } from "types";
import { NoResults } from "../../../Common/NoResults";
import { NoTranslate } from "../../../Common/NoTranslate";
import { ProductImage } from "../../../Common/ProductImage";
import { PartnerBadge, PartnerSellerBadge } from "../PartnerBadge";
import { CollectionImage } from "@/components/Product/GlobalSettings/Collections/CollectionImage";
import { useSeller } from "@/hooks/useAccount";

/**
 * Props for DragAndDropComponent.
 * Optionally accepts a renderItem function to customize how each product is rendered.
 * When enabledProductObjects is provided, both columns are filtered to show only products of those types.
 */
interface DragAndDropComponentProps {
    data: Product[];
    displayedProducts: Product[];
    notDisplayedSearchValue: string;
    displayedSearchValue: string;
    enabledProductObjects?: ProductObject[];
    showPackageBadges?: boolean;
    showMerchandiseBadges?: boolean;
    onMove: (data: Product[][]) => void;
    renderItem?: (
        product: Product,
        index: number,
        dragProps: { onDragStart: (e: any, id: ID) => void; onDragEnter: () => void },
    ) => ReactNode;
}

/**
 * Generic DragAndDropComponent that can be used for both ButtonCode and Kiosk.
 * If renderItem is not provided, uses the ButtonCode default rendering.
 */
export const DragDropComponent = ({
    data,
    displayedProducts,
    onMove,
    notDisplayedSearchValue,
    displayedSearchValue,
    enabledProductObjects,
    showPackageBadges = false,
    showMerchandiseBadges = false,
    renderItem,
}: DragAndDropComponentProps) => {
    const [reorderIndexes, setReorderIndexes] = useState({
        fromIndex: 0,
        toIndex: 0,
    });
    const [draggableData, setDraggableData] = useState({
        notDisplayed: data,
        displayed: displayedProducts,
    });

    const onDragStart = (event: any, id: ID, source: "notDisplayed" | "displayed", index: number) => {
        event.dataTransfer.setData("id", id);
        event.dataTransfer.setData("source", source);
        setReorderIndexes((previousState) => ({
            ...previousState,
            fromIndex: index,
        }));
    };

    const notDisplayChanged = data.length !== draggableData.notDisplayed.length;
    const displayChanged = displayedProducts.length !== draggableData.displayed.length;
    const shouldInvokeOnMove = notDisplayChanged || displayChanged;
    useEffect(() => {
        if (shouldInvokeOnMove) {
            onMove([draggableData.notDisplayed, draggableData.displayed]);
        }
    }, [draggableData.notDisplayed, draggableData.displayed, onMove, shouldInvokeOnMove]);

    const onDrop = (event: any, destination: "notDisplayed" | "displayed") => {
        const id = event.dataTransfer.getData("id");
        const source: "notDisplayed" | "displayed" = event.dataTransfer.getData("source");

        if (id && source !== destination) {
            const item = draggableData[source].find((product: Product) => product.id === id);
            const sourceData = draggableData[source].filter((product: Product) => product.id !== id);
            const destinationData = [item!, ...draggableData[destination]];
            setDraggableData({
                ...draggableData,
                [source]: sourceData,
                [destination]: destinationData,
            });
        }

        if (id && source === destination) {
            const currentEle = { ...draggableData[source][reorderIndexes.fromIndex] };
            const divs = draggableData[source].filter((_value, index) => {
                return index !== reorderIndexes.fromIndex;
            });
            divs.splice(reorderIndexes.toIndex, 0, currentEle);
            if (source === "displayed") {
                setDraggableData({
                    notDisplayed: [...draggableData.notDisplayed],
                    displayed: [...divs],
                });
                onMove([[...draggableData.notDisplayed], [...divs]]);
            } else {
                setDraggableData({
                    notDisplayed: [...divs],
                    displayed: [...draggableData.displayed],
                });
                onMove([[...divs], [...draggableData.displayed]]);
            }
        }
    };

    let { notDisplayed: filteredNotDisplayed, displayed: filteredDisplayed } = draggableData;

    // Filter by enabled product types when provided (empty array = show no products)
    if (enabledProductObjects !== undefined) {
        const enabledSet = new Set(enabledProductObjects);
        filteredNotDisplayed = filteredNotDisplayed.filter((p) => enabledSet.has(p.object));
        filteredDisplayed = filteredDisplayed.filter((p) => enabledSet.has(p.object));
    }

    if (notDisplayedSearchValue && notDisplayedSearchValue !== "") {
        filteredNotDisplayed = notDisplayedSearchValue
            ? filteredNotDisplayed.filter((exp: Product) =>
                  exp.name.trim().toLowerCase().includes(notDisplayedSearchValue.trim().toLowerCase()),
              )
            : filteredNotDisplayed;
    }

    if (displayedSearchValue && displayedSearchValue !== "") {
        filteredDisplayed = displayedSearchValue
            ? filteredDisplayed.filter((exp: Product) =>
                  exp.name.trim().toLowerCase().includes(displayedSearchValue.trim().toLowerCase()),
              )
            : filteredDisplayed;
    }

    // Default item renderer (ButtonCode style)
    const seller = useSeller();
    const defaultRenderItem = (
        item: Product,
        _index: number,
        dragProps: { onDragStart: (e: any, id: ID) => void; onDragEnter: () => void },
    ) => {
        const collectionItem = (item as Product & { collection?: CollectionNode }).collection;
        const isPartnerExperience = item.object === ProductObject.EXPERIENCE && item.seller.id !== seller.id;
        const isPartnerPackage = isPartnerPackageProduct(item);
        const isPartnerProduct = isPartnerExperience || isPartnerPackage;
        const partnerSellerId = isPartnerProduct ? item.seller.id : null;
        return (
            <div
                key={item.id}
                draggable
                className="border-b border-b-gray-light bg-white p-2"
                onDragEnter={dragProps.onDragEnter}
                onDragStart={(e) => dragProps.onDragStart(e, item.id)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        {item.object === ProductObject.COLLECTION && collectionItem ? (
                            <CollectionImage collection={collectionItem} size="small" />
                        ) : isBaseGiftProduct(item) ? (
                            <div className="mr-3 flex-shrink-0 rounded-lg border border-gray bg-white p-2">
                                <GiftIcon size="large" />
                            </div>
                        ) : (
                            <ProductImage product={item} size="small" />
                        )}
                        <div className="space-y-2">
                            <NoTranslate as="div" className="font-semibold">
                                {item.name}
                            </NoTranslate>
                            {item.object === ProductObject.COLLECTION ? (
                                <Badge color="secondary" className="flex w-fit items-center gap-1">
                                    <CollectionIcon size="small" />
                                    <span>Collection</span>
                                </Badge>
                            ) : null}
                            {isPartnerProduct ? (
                                <div className="flex items-center gap-2">
                                    <PartnerBadge isPackage={isPartnerPackage} />
                                    {partnerSellerId && <PartnerSellerBadge sellerId={partnerSellerId} />}
                                </div>
                            ) : showPackageBadges && isPackage(item) ? (
                                <Badge color="secondary" className="flex-0 space-x-2">
                                    Package
                                </Badge>
                            ) : showMerchandiseBadges && isMerchandiseProduct(item) ? (
                                <Badge color="secondary" className="flex-0 space-x-2">
                                    Merchandise
                                </Badge>
                            ) : null}
                        </div>
                    </div>
                    <MenuIcon size="small" />
                </div>
            </div>
        );
    };

    return (
        <div className="flex w-full justify-between space-x-4">
            <DraggableItem onDragOver={onDragOver} onDrop={(e) => onDrop(e, "notDisplayed")}>
                {filteredNotDisplayed.length === 0 ? (
                    <NoResults>No results found</NoResults>
                ) : (
                    filteredNotDisplayed.map((product, index) =>
                        renderItem
                            ? renderItem(product, index, {
                                  onDragStart: (e, id) => onDragStart(e, id, "notDisplayed", index),
                                  onDragEnter: () =>
                                      setReorderIndexes((previousState) => ({
                                          ...previousState,
                                          toIndex: index,
                                      })),
                              })
                            : defaultRenderItem(product, index, {
                                  onDragStart: (e, id) => onDragStart(e, id, "notDisplayed", index),
                                  onDragEnter: () =>
                                      setReorderIndexes((previousState) => ({
                                          ...previousState,
                                          toIndex: index,
                                      })),
                              }),
                    )
                )}
            </DraggableItem>
            <DraggableItem onDragOver={onDragOver} onDrop={(e) => onDrop(e, "displayed")}>
                {filteredDisplayed.map((product, index) =>
                    renderItem
                        ? renderItem(product, index, {
                              onDragStart: (e, id) => onDragStart(e, id, "displayed", index),
                              onDragEnter: () =>
                                  setReorderIndexes((previousState) => ({
                                      ...previousState,
                                      toIndex: index,
                                  })),
                          })
                        : defaultRenderItem(product, index, {
                              onDragStart: (e, id) => onDragStart(e, id, "displayed", index),
                              onDragEnter: () =>
                                  setReorderIndexes((previousState) => ({
                                      ...previousState,
                                      toIndex: index,
                                  })),
                          }),
                )}
            </DraggableItem>
        </div>
    );
};

const onDragOver = (event: any) => {
    event.preventDefault();
};

interface DraggableItemProps {
    onDragOver: (e: any) => void;
    onDrop: (e: any) => void;
    children: ReactNode;
}

const DraggableItem = ({ onDragOver, onDrop, children }: DraggableItemProps) => (
    <div className="h-80 w-full flex-1 flex-col overflow-y-scroll" onDragOver={onDragOver} onDrop={onDrop}>
        {children}
    </div>
);
